import { useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import {
  FaFolderOpen,
  FaChevronRight,
  FaEllipsisV,
  FaPencilAlt,
  FaUserPlus,
  FaEyeSlash,
  FaTrash,
  FaRegCheckCircle,
  FaRegTimesCircle,
} from "react-icons/fa";
import { getTotale, formatRelativeDate } from "./utils/helpers";

/**
 * SchedaRow - Compact "vertical stack" summary row for a scheda, styled after
 * the Stitch "Gestione Schede - Vertical Stack Variant" reference screen.
 * Replaces SchedaHeader's non-editing display; title-edit/share/hide/delete
 * actions are tucked behind a kebab menu so the row stays a single click
 * target that navigates to the scheda detail page.
 */
const SchedaRow = ({
  scheda,
  // Title editing props (from useTitleEdit)
  isEditing,
  titolo,
  erroLength,
  onLongPress,
  onChange,
  cancelEdit,
  updateSchedataTitolo,
  // Action handlers
  onShare,
  onDelete,
  onHide,
  // Click handler for navigating to the scheda detail page
  onTitleClick,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  if (isEditing) {
    return (
      <div className="scheda-row" style={{ cursor: "default" }}>
        <div className="scheda-row-main" style={{ flex: 1 }}>
          <div className="scheda-row-icon">
            <FaFolderOpen size={20} />
          </div>
          <Form.Group style={{ flex: 1 }}>
            <Form.Control
              type="text"
              id="titolo"
              maxLength="21"
              name="titolo"
              value={titolo}
              placeholder="titolo"
              onChange={onChange}
              autoFocus
            />
            {erroLength && (
              <small className="text-danger">Limit characters reached</small>
            )}
          </Form.Group>
        </div>
        <div className="scheda-row-end">
          <button
            type="button"
            className="scheda-row-actions-toggle"
            onClick={cancelEdit}
          >
            <FaRegTimesCircle size={16} />
          </button>
          <button
            type="button"
            className="scheda-row-actions-toggle"
            onClick={updateSchedataTitolo}
          >
            <FaRegCheckCircle size={16} />
          </button>
        </div>
      </div>
    );
  }

  const activityCount = scheda.notaSpese?.length || 0;
  const updatedLabel = formatRelativeDate(scheda.updatedAt || scheda.createdAt);

  return (
    <div className="scheda-row" onClick={onTitleClick}>
      <div className="scheda-row-main">
        <div className="scheda-row-icon">
          <FaFolderOpen size={20} />
        </div>
        <div style={{ minWidth: 0 }}>
          <p className="scheda-row-title mb-0">{scheda.titolo}</p>
          <p className="scheda-row-meta mb-0">
            {activityCount} {activityCount === 1 ? "activity" : "activities"}
            {updatedLabel ? ` • updated ${updatedLabel}` : ""}
          </p>
          {scheda.myRole && (
            <span
              className="scheda-row-badge scheda-row-badge--role mt-1"
            >
              {scheda.myRole}
            </span>
          )}
        </div>
      </div>
      <div className="scheda-row-end">
        <div className="text-end">
          <p className="scheda-row-amount-label">Total</p>
          <p className="scheda-row-amount-value">€ {getTotale(scheda.notaSpese)}</p>
        </div>
        <div
          className="scheda-row-actions"
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="scheda-row-actions-toggle"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Scheda actions"
          >
            <FaEllipsisV size={14} />
          </button>
          {menuOpen && (
            <div className="scheda-row-actions-menu">
              <button
                type="button"
                onClick={() => {
                  onLongPress();
                  setMenuOpen(false);
                }}
              >
                <FaPencilAlt size={12} /> Edit title
              </button>
              <button
                type="button"
                onClick={() => {
                  onShare();
                  setMenuOpen(false);
                }}
              >
                <FaUserPlus size={12} /> Share
              </button>
              {onHide && scheda.myPermissions?.canNascodereScheda && (
                <button
                  type="button"
                  onClick={() => {
                    onHide();
                    setMenuOpen(false);
                  }}
                >
                  <FaEyeSlash size={12} /> Hide
                </button>
              )}
              {scheda.myPermissions?.canEliminareScheda && (
                <button
                  type="button"
                  className="text-danger"
                  onClick={() => {
                    onDelete();
                    setMenuOpen(false);
                  }}
                >
                  <FaTrash size={12} /> Delete
                </button>
              )}
            </div>
          )}
        </div>
        <FaChevronRight className="text-muted d-none d-sm-block" size={14} />
      </div>
    </div>
  );
};

export default SchedaRow;
